import Table, { Header } from 'bam-node-savant-table/table-ts.ts'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isComparator } from './types.ts'
import type {
    Filter,
    PercentileOption,
    PercentileRankingsRow,
} from './types.ts'
import FilterRow from './FilterRow.tsx'
import FilterPills from './FilterPills.tsx'

// Query-string key holding the active percentile filters. Each occurrence is a
// `prop-comparator-value` triple (e.g. `pctl=percent_rank_xwoba-gte-75`). A
// repeated key keeps the filters ordered and avoids parallel-array misalignment
// while leaving every other query param untouched.
const FILTER_PARAM = 'pctl'
const FILTERED_COLUMN_CLASS = 'percentile-filtered-column'

// Custom event dispatched from the raw page markup (the "Edit Filters" button)
// to switch this React app into editing mode.
export const EDIT_FILTERS_EVENT = 'percentile-edit-filters'

// Serialize the active filters into the repeated `pctl` token format.
function filtersToParam(filters: Filter[]): string[] {
    return filters.map(
        (filter) => `${filter.prop}-${filter.comparator}-${filter.val}`
    )
}

// Parse the `pctl` params back into filters, dropping anything invalid: unknown
// props, bad comparators, out-of-range values, and duplicate props (the table
// supports a single filter per column).
function paramToFilters(
    searchParams: URLSearchParams,
    options: PercentileOption[],
    makeId: () => string
): Filter[] {
    const validProps = new Set(options.map((option) => option.value))
    const usedProps = new Set<string>()
    const filters: Filter[] = []

    searchParams.getAll(FILTER_PARAM).forEach((raw) => {
        // Props use underscores, comparators are letters, and the value is a
        // number, so the triple always splits cleanly into exactly 3 parts.
        const parts = raw.split('-')
        if (parts.length !== 3) {
            return
        }

        const [prop, comparator, strVal] = parts
        const val = Number(strVal)

        if (!validProps.has(prop) || usedProps.has(prop)) {
            return
        }
        if (!isComparator(comparator)) {
            return
        }
        if (!Number.isFinite(val) || val < 0 || val > 100) {
            return
        }

        usedProps.add(prop)
        filters.push({
            id: makeId(),
            prop,
            comparator,
            val,
        })
    })

    return filters
}

// Build the percentile dropdown options from the same headers that define the
// table columns, so the filter and the table never drift out of sync.
function getPercentileOptions(
    headers: Header<PercentileRankingsRow>[]
): PercentileOption[] {
    return headers
        .filter(
            (
                header
            ): header is Header<PercentileRankingsRow> & { field: string } =>
                typeof header.field === 'string' &&
                header.field.startsWith('percent_')
        )
        .map((header) => ({
            value: header.field,
            label: header.name
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim(),
        }))
}

function highlightFilteredColumns(
    table: Table<PercentileRankingsRow>,
    filters: Filter[]
) {
    table.sel
        .querySelectorAll(`.${FILTERED_COLUMN_CLASS}`)
        .forEach((el) => el.classList.remove(FILTERED_COLUMN_CLASS))

    const filteredProps = new Set(filters.map((filter) => filter.prop))

    table.headers.forEach((header, headerIndex) => {
        if (
            typeof header.field !== 'string' ||
            !filteredProps.has(header.field)
        ) {
            return
        }

        const columnPosition = headerIndex + (table.showRank ? 2 : 1)

        table.sel
            .querySelectorAll(
                [
                    `thead tr > th:nth-child(${columnPosition})`,
                    `tbody tr.tr-component-row > th:nth-child(${columnPosition})`,
                    `tbody tr.default-table-row > td:nth-child(${columnPosition})`,
                ].join(',')
            )
            .forEach((el) => el.classList.add(FILTERED_COLUMN_CLASS))
    })
}

type FilterAppProps = {
    table: Table<PercentileRankingsRow>
}

export default function FilterApp({ table }: FilterAppProps) {
    const options = useMemo(() => getPercentileOptions(table.headers), [table])
    const nextId = useRef(0)

    function makeId(): string {
        nextId.current += 1
        return `filter-${nextId.current}`
    }

    function createFilter(prop: string): Filter {
        return {
            id: makeId(),
            prop,
            comparator: 'gte',
            val: 0,
        }
    }

    // Restore filters from the URL on first render so shared/bookmarked links
    // render with the table already filtered.
    const [filters, setFilters] = useState<Filter[]>(() =>
        paramToFilters(
            new URLSearchParams(window.location.search),
            options,
            makeId
        )
    )
    const filtersRef = useRef(filters)

    const [isEditing, setIsEditing] = useState(false)

    // The table only supports a single filter per column, so each percentile
    // column may be used by at most one row.
    const usedProps = new Set(filters.map((filter) => filter.prop))
    const firstUnusedOption = options.find(
        (option) => !usedProps.has(option.value)
    )

    const isDisabled = !firstUnusedOption

    function handleFilterChange(next: Filter, idx: number) {
        setFilters((prev) => prev.map((f, i) => (i === idx ? next : f)))
    }

    function handleAddFilter() {
        if (!firstUnusedOption) {
            return
        }
        setFilters((prev) => [...prev, createFilter(firstUnusedOption.value)])
        setIsEditing(true)
    }

    function handleRemoveFilter(idx: number) {
        setFilters((prev) => prev.filter((_, i) => i !== idx))
    }

    useEffect(() => {
        if (filters.length === 0 && isEditing) {
            setIsEditing(false)
        }
    }, [filters.length, isEditing])

    // Listen for the "Edit Filters" button in the raw page markup. The button
    // toggles editing mode. When turning editing on with no filters yet, seed
    // one so the user has something to edit (mirroring "Click Here to add a
    // filter").
    useEffect(() => {
        function handleEditFiltersEvent() {
            setIsEditing((prevEditing) => {
                const nextEditing = !prevEditing
                if (nextEditing) {
                    setFilters((prev) => {
                        if (prev.length > 0) {
                            return prev
                        }
                        const unusedOption = options.find(
                            (option) =>
                                !prev.some((f) => f.prop === option.value)
                        )
                        return unusedOption
                            ? [createFilter(unusedOption.value)]
                            : prev
                    })
                }
                return nextEditing
            })
        }

        window.addEventListener(EDIT_FILTERS_EVENT, handleEditFiltersEvent)

        return () => {
            window.removeEventListener(
                EDIT_FILTERS_EVENT,
                handleEditFiltersEvent
            )
        }
    }, [options])

    // Keep the raw "Edit Filters" button label in sync with the editing state
    // so it reads "Finish Editing" while the filters are open.
    useEffect(() => {
        const button = document.getElementById('edit-button-text')
        if (button) {
            button.textContent = isEditing
                ? '📊 Finish Editing'
                : '📊 Edit Filters'
        }
    }, [isEditing])

    useEffect(() => {
        const percentileTable = table
        const previousDrawCallback = percentileTable.drawCallback
        const nextDrawCallback = (drawnTable: Table<PercentileRankingsRow>) => {
            previousDrawCallback?.(drawnTable)
            highlightFilteredColumns(drawnTable, filtersRef.current)
        }

        percentileTable.drawCallback = nextDrawCallback

        return () => {
            if (percentileTable.drawCallback === nextDrawCallback) {
                percentileTable.drawCallback = previousDrawCallback
            }
        }
    }, [table])

    useEffect(() => {
        const percentileTable = table
        filtersRef.current = filters

        percentileTable.resetColumnFilters()
        // In the event there are no filters
        percentileTable.drawTable()
        filters.forEach((filter) => {
            percentileTable.updateColumnFilter(
                filter.prop,
                filter.comparator,
                filter.val
            )
        })
        highlightFilteredColumns(percentileTable, filters)

        // Keep the URL in sync, preserving every unrelated query param. Use
        // replaceState (not pushState) so the number input typing doesn't flood
        // browser history.
        const params = new URLSearchParams(window.location.search)
        params.delete(FILTER_PARAM)
        filtersToParam(filters).forEach((value) =>
            params.append(FILTER_PARAM, value)
        )

        const query = params.toString()
        const nextUrl = `${window.location.pathname}${
            query ? `?${query}` : ''
        }${window.location.hash}`
        window.history.replaceState(null, '', nextUrl)
    }, [filters, table])

    if (isEditing) {
        return (
            <div className="savant-docs-card" style={{ overflowX: 'auto' }}>
                <div style={{ display: 'flex', marginBottom: '1rem' }}>
                    <h4 style={{ marginRight: '2rem' }}>Filters</h4>
                    <div>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleAddFilter}
                            disabled={isDisabled}
                            style={
                                {
                                    marginRight: '10px',
                                    color: '#fff',
                                    '--emoji-color': '#fff',
                                } as React.CSSProperties
                            }
                        >
                            <span className="mono-emoji">➕</span> Add Filter
                        </button>
                        <button
                            type="button"
                            className="btn btn-default"
                            onClick={() => setIsEditing(false)}
                        >
                            Finish Editing
                        </button>
                    </div>
                </div>
                {filters.map((f, idx) => {
                    // Exclude columns already chosen by other rows so a filter can
                    // never be changed into one that already exists.
                    const usedByOthers = new Set(
                        filters
                            .filter((_, i) => i !== idx)
                            .map((other) => other.prop)
                    )
                    const availableOptions = options.filter(
                        (option) => !usedByOthers.has(option.value)
                    )

                    return (
                        <FilterRow
                            key={f.id}
                            filter={f}
                            options={availableOptions}
                            onChange={(next) => handleFilterChange(next, idx)}
                            onRemove={() => handleRemoveFilter(idx)}
                        />
                    )
                })}
            </div>
        )
    }

    if (!filters.length) {
        return null
    }

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center',
            }}
        >
            <h4>Filters:</h4>
            {filters.map((f) => (
                <FilterPills key={f.id} filter={f} options={options} />
            ))}
        </div>
    )
}
